import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { RoleProvider } from './hooks/useRole';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { ToastProvider } from './components/ui/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';
import './index.css';
import './styles/theme.css';
import './styles/color-picker.css';

const LoginForm = lazy(() => import('./components/auth/LoginForm'));
const RegisterForm = lazy(() => import('./components/auth/RegisterForm'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const ColorPickerTest = lazy(() => import('./components/test/ColorPickerTest'));

const RouteLoadingFallback: React.FC = () => (
  <div className="flex min-h-[40vh] items-center justify-center px-4 py-12">
    <div className="rounded-3xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
      <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#1C3557]" />
      <p className="mt-3 text-sm font-medium text-slate-600">Ładowanie widoku...</p>
    </div>
  </div>
);

// Komponent ochrony tras wymagających autoryzacji
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useApp();

  if (!state.isAuthReady) {
    return <RouteLoadingFallback />;
  }
  
  if (!state.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Komponent przekierowania zalogowanych użytkowników
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useApp();

  if (!state.isAuthReady) {
    return <RouteLoadingFallback />;
  }
  
  if (state.isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

// Root layout component
const RootLayout: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AppProvider>
            <RoleProvider>
              <div className="App">
                <PWAInstallPrompt />
                <ErrorBoundary fallback={
                  <div className="p-4 text-center">
                    <h2 className="text-lg font-semibold text-red-600">Błąd komponentu</h2>
                    <p className="text-sm text-gray-600">Problem z renderowaniem zawartości strony</p>
                  </div>
                }>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <Outlet />
                  </Suspense>
                </ErrorBoundary>
              </div>
            </RoleProvider>
          </AppProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

// Router configuration for React Router v7
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "login",
        element: (
          <PublicRoute>
            <LoginForm />
          </PublicRoute>
        ),
      },
      {
        path: "register",
        element: (
          <PublicRoute>
            <RegisterForm />
          </PublicRoute>
        ),
      },
      {
        path: "dashboard",
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "color-test",
        element: <ColorPickerTest />,
      },
      {
        path: "*",
        element: <Navigate to="/dashboard" replace />,
      },
    ],
  },
]);

// Główny komponent App
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
};

export default App;
